import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowControls } from '../../components';
import { useAnalytics, useAnalyticsAttributes } from '../hooks';
import { DesktopControls, Dot, Dots, Frame, Mask, MobileControls, Slide, Track } from './Carousel.styles';
export function Carousel({ arrows, dotsOnDesktop, dotsOnMobile, children, onChange, selectedIndex, initialAutoplay = false, transitionTime = 0.3, dotLabel = 'Dot', prevArrowLabel = 'Previous', nextArrowLabel = 'Next', controlsAlignStart = false }) {
    //current slide #
    const [slide, setSlide] = useState(selectedIndex ? selectedIndex : 0);
    //current track offset
    const [marginLeft, setMarginLeft] = useState(0);
    //drag information (start of drag, margin at start)
    const [drag, setDrag] = useState(null);
    //current frame size
    const [frameWidth, setFrameWidth] = useState(0);
    const [preventScroll, setPreventScroll] = useState(false);
    const [autoplay, setAutoplay] = useState(initialAutoplay);
    //frame ref
    const frame = useRef(null);
    //analytics
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();
    //enforce array-ness of children
    const slides = !children
        ? []
        : !Array.isArray(children)
            ? [children]
            : children;
    const hasMultipleSlides = slides.length > 1;
    const gapSize = 16;
    //maintain current width of the frame
    useEffect(() => {
        const handleResize = () => {
            var _a, _b;
            setFrameWidth((_b = (_a = frame.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [frame]);
    //prevent vertical scrolling when sliding carousel
    useEffect(() => {
        if (preventScroll) {
            document.body.classList.add('prevent-scroll');
            document.ontouchmove = (e) => {
                e.preventDefault();
            };
        }
        else {
            document.body.classList.remove('prevent-scroll');
            document.ontouchmove = () => true;
        }
        return () => {
            document.body.classList.remove('prevent-scroll');
            document.ontouchmove = () => true;
        };
    }, [preventScroll]);
    //listen to slide updates
    useEffect(() => {
        sendTrackEvent('carouselChanged', Object.assign(Object.assign({}, analyticsAttributes), { eventComponent: 'carousel', event: 'changed', label: `Slide ${slide + 1}`, labelEnglish: `Slide ${slide + 1}` }));
        if (onChange)
            onChange(slide);
    }, [slide]);
    //change slide from parent element
    useEffect(() => {
        if (selectedIndex !== undefined && selectedIndex !== slide) {
            //stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
            setSlide(selectedIndex);
        }
    }, [selectedIndex]);
    //handle autoplay
    useEffect(() => {
        const timeout = autoplay && initialAutoplay
            ? setTimeout(() => {
                nextSlide();
            }, 5000)
            : null;
        return () => {
            if (timeout)
                clearTimeout(timeout);
        };
    }, [autoplay, initialAutoplay, slide]);
    //go to the current slide
    const goToSlide = () => {
        setMarginLeft(0 - slide * frameWidth - slide * gapSize);
    };
    //set margin when window resizes or slide changes
    useEffect(goToSlide, [slide, frameWidth]);
    //go to previous slide
    const prevSlide = (e) => {
        if (!hasMultipleSlides)
            return;
        if (e) {
            //arrow was clicked; stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
        }
        setSlide((slide ? slide : slides.length) - 1);
    };
    //go to next slide
    const nextSlide = (e) => {
        if (!hasMultipleSlides)
            return;
        if (e) {
            //arrow was clicked; stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
        }
        setSlide(slide + 1 === slides.length ? 0 : slide + 1);
    };
    const dragStart = (x, y) => {
        setDrag({ dragStart: [x, y], startingMargin: marginLeft });
    };
    const dragging = (x, y) => {
        //check if drag started on carousel
        if (drag === null)
            return;
        //stop autoplay if it's running
        if (autoplay)
            setAutoplay(false);
        //exit if dragging vertically
        const horizontalOffset = x - drag.dragStart[0];
        const verticalOffset = y - drag.dragStart[1];
        if (Math.abs(horizontalOffset) < Math.abs(verticalOffset))
            return;
        //horizontal touch, lock vertical scroll
        if (!preventScroll)
            setPreventScroll(true);
        //move track
        const dragAmount = horizontalOffset + drag.startingMargin;
        const maxAmount = 0;
        const minAmount = 0 - frameWidth * (slides.length - 1) - (slides.length - 1) * gapSize;
        const friction = 3;
        const amountWithFriction = dragAmount > maxAmount
            ? dragAmount / friction
            : dragAmount < minAmount
                ? minAmount + (dragAmount - minAmount) / friction
                : dragAmount;
        setMarginLeft(amountWithFriction);
    };
    const dragEnd = (x) => {
        if (drag !== null) {
            const dragAmount = drag.dragStart[0] - x;
            const dragDistance = frameWidth / 3; //more than one third
            setDrag(null);
            setPreventScroll(false);
            if (dragAmount > dragDistance && slide !== slides.length - 1) {
                //go to next item
                nextSlide();
            }
            else if (dragAmount < 0 - dragDistance && slide) {
                //go to previous item
                prevSlide();
            }
            else {
                //restore current item
                goToSlide();
            }
        }
    };
    const dotsControls = (React.createElement(Dots, { mobile: dotsOnMobile, desktop: dotsOnDesktop }, slides.map((child, index) => !!child && (React.createElement(Dot, { key: index, "data-selected": index === slide, "data-testid": `dot_${index}`, "aria-label": `${dotLabel} ${index + 1}`, onClick: () => {
            //stop autoplay if it's running
            if (autoplay)
                setAutoplay(false);
            setSlide(index);
        }, transitionTime: transitionTime })))));
    const showMobileControls = useMemo(() => hasMultipleSlides && (dotsOnMobile || arrows), [hasMultipleSlides, dotsOnMobile, arrows]);
    const showDesktopControls = useMemo(() => hasMultipleSlides && (dotsOnDesktop || arrows), [hasMultipleSlides, dotsOnDesktop, arrows]);
    return (React.createElement(Frame, { "data-testid": "carousel", style: {
            visibility: frameWidth ? 'visible' : 'hidden'
        }, ref: frame },
        showDesktopControls && (React.createElement(DesktopControls, { alignStart: controlsAlignStart },
            dotsOnDesktop && dotsControls,
            arrows && (React.createElement(ArrowControls, { next: nextSlide, nextLabel: nextArrowLabel, prev: prevSlide, prevLabel: prevArrowLabel })))),
        React.createElement(Mask, null,
            React.createElement(Track, { style: {
                    cursor: drag ? 'grabbing' : 'grab',
                    gap: gapSize,
                    marginLeft: marginLeft,
                    transition: drag ? 'none' : `margin-left ${transitionTime}s ease`,
                    width: frameWidth * slides.length + (slides.length - 1) * gapSize
                }, onTouchStart: ({ touches }) => {
                    if (touches[0]) {
                        dragStart(touches[0].clientX, touches[0].clientY);
                    }
                }, onTouchMove: ({ touches }) => {
                    if (touches[0]) {
                        dragging(touches[0].clientX, touches[0].clientY);
                    }
                }, onTouchEnd: ({ changedTouches }) => {
                    if (changedTouches[0]) {
                        dragEnd(changedTouches[0].clientX);
                    }
                }, onMouseDown: (event) => {
                    dragStart(event.clientX, event.clientY);
                }, onMouseMove: (event) => {
                    dragging(event.clientX, event.clientY);
                }, onMouseUp: (event) => {
                    dragEnd(event.clientX);
                }, onMouseLeave: (event) => {
                    dragEnd(event.clientX);
                } }, slides.map((child, index) => (React.createElement(Slide, { "aria-hidden": slide !== index, key: index }, child))))),
        showMobileControls && (React.createElement(MobileControls, null, dotsOnMobile && dotsControls))));
}
//# sourceMappingURL=Carousel.js.map